import {
  Archive,
  Calendar,
  Drama,
  Eye,
  FileText,
  MapPin,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';

import {
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import { cn } from '@/helpers/shadCnUtils';

// Level indicator configuration
export const getInconsistencyLevelConfig = (level: InconsistencyLevel) => {
  const config = {
    [InconsistencyLevel.Minor]: {
      Icon: ShieldQuestion,
      label: 'Minor',
      className: 'bg-yellow-100 hover:bg-yellow-200/80',
      color: 'text-yellow-600',
    },
    [InconsistencyLevel.Severe]: {
      Icon: ShieldCheck,
      label: 'Severe',
      className: 'bg-orange-200 hover:bg-orange-300/80',
      color: 'text-orange-600',
    },
    [InconsistencyLevel.Critical]: {
      Icon: ShieldAlert,
      label: 'Critical',
      className: 'bg-red-300 hover:bg-red-400/80',
      color: 'text-red-600',
    },
  };

  return config[level];
};

// Source indicator configuration
export const getInconsistencySourceConfig = (source: InconsistencySource) => {
  const config = {
    [InconsistencySource.MultipleDepositions]: {
      label: getInconsistencySourceDisplayName(InconsistencySource.MultipleDepositions),
      className: 'bg-purple-100 border border-purple-200 hover:bg-purple-200/80 text-purple-900',
    },
    [InconsistencySource.SameDeposition]: {
      label: getInconsistencySourceDisplayName(InconsistencySource.SameDeposition),
      className: 'bg-blue-100 border border-blue-200 hover:bg-blue-200/80 text-blue-900',
    },
    [InconsistencySource.DepositionVsEvidence]: {
      label: getInconsistencySourceDisplayName(InconsistencySource.DepositionVsEvidence),
      className: 'bg-orange-100 border border-orange-200 hover:bg-orange-200/80 text-orange-900',
    },
    [InconsistencySource.EvidenceVsEvidence]: {
      label: getInconsistencySourceDisplayName(InconsistencySource.EvidenceVsEvidence),
      className: 'bg-pink-100 border border-pink-200 hover:bg-pink-200/80 text-pink-900',
    },
  };

  return config[source];
};

// Type indicator configuration
export const getInconsistencyTypeConfig = (type: InconsistencyType) => {
  const config = {
    [InconsistencyType.Dates]: {
      Icon: Calendar,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Dates),
      className: 'bg-purple-100 hover:bg-purple-200/80 text-purple-900',
    },
    [InconsistencyType.Events]: {
      Icon: MapPin,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Events),
      className: 'bg-blue-100 hover:bg-blue-200/80 text-blue-900',
    },
    [InconsistencyType.Evolving]: {
      Icon: RefreshCw,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Evolving),
      className: 'bg-green-100 hover:bg-green-200/80 text-green-900',
    },
    [InconsistencyType.Details]: {
      Icon: FileText,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Details),
      className: 'bg-yellow-100 hover:bg-yellow-200/80 text-yellow-900',
    },
    [InconsistencyType.Observations]: {
      Icon: Eye,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Observations),
      className: 'bg-pink-100 hover:bg-pink-200/80 text-pink-900',
    },
    [InconsistencyType.Fabrications]: {
      Icon: Drama,
      label: getInconsistencyTypeDisplayName(InconsistencyType.Fabrications),
      className: 'bg-red-100 hover:bg-red-200/80 text-red-900',
    },
  };

  return config[type];
};

// Evidence count indicator component
export const EvidenceCountIndicator = ({ count, className }: { count: number; className?: string }) => {
  return (
    <div className={cn('group rounded-md border border-gray-200 bg-gray-50 px-3 py-2', className)}>
      <div className="flex items-center gap-2">
        <Archive className="h-3 w-3 text-gray-500" />
        <span>
          <b className="text-gray-700">{count}</b>
          <span className="ml-1 text-gray-600">pieces of evidence</span>
        </span>
      </div>
    </div>
  );
};

// Color scheme for fact groups
export const getFactGroupColors = () => [
  {
    bg: 'bg-blue-50/50',
    line: 'bg-blue-200',
  },
  {
    bg: 'bg-red-50/50',
    line: 'bg-red-200',
  },
  {
    bg: 'bg-purple-50/50',
    line: 'bg-purple-200',
  },
  {
    bg: 'bg-orange-50/50',
    line: 'bg-orange-200',
  },
];
